import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Shield, Award, CheckCircle, Star, Clock } from "lucide-react";

const FLORIDA_IMG = "https://images.unsplash.com/photo-1672619645030-f09246fa77c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxmbG9yaWRhJTIwc3dhbXAlMjBuYXR1cmV8ZW58MHx8fHwxNzc0MDM3MjQ3fDA&ixlib=rb-4.1.0&q=85";

const credentials = [
  { icon: Award, text: "50 Years of Hands-On Florida Wildlife Experience" },
  { icon: Shield, text: "Registered LLC — Fully Licensed & Insured" },
  { icon: CheckCircle, text: "Federal Swine Dealers License #FL 04513-16" },
  { icon: CheckCircle, text: "Certified Swine Facility Operator" },
  { icon: Star, text: "100% Satisfaction Guaranteed" },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About Double D Trapping LLC | 50 Years Experience | St. Augustine, FL";
  }, []);

  return (
    <>
      {/* Hero with image */}
      <section className="relative" data-testid="about-page-header">
        <div className="absolute inset-0">
          <img src={FLORIDA_IMG} alt="Florida nature" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B110E] via-[#0B110E]/85 to-[#0B110E]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">About Us</p>
          <h1 className="text-[#F8FAFC] font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="about-page-title">
            About Double D Trapping LLC
          </h1>
          <p className="text-[#D95D39] font-bold text-base sm:text-lg tracking-wide">
            Not a Franchise. Not a Call Center. Just 50 Years of Getting It Done.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#0B110E] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[#9CA3AF] text-base sm:text-lg leading-relaxed space-y-6 mb-16">
            <p>
              D. Davis started trapping Florida wildlife before most people knew what an armadillo was. Five decades later, Double D Trapping LLC is one of the most experienced and credentialed wildlife removal operations in Northeast Florida — and it's still personal.
            </p>
            <p>
              When you call, you get D. Davis. When we show up, it's someone who has seen every situation, solved every problem, and takes pride in leaving your home better than we found it.
            </p>
            <p>
              We're not here to upsell you or lock you into a contract. We're here to remove the animal, seal the home, clean up the mess, and make sure it doesn't happen again.
            </p>
          </div>

          {/* Credentials */}
          <div className="mb-16">
            <h2 className="text-[#F8FAFC] font-black text-2xl sm:text-3xl tracking-tighter mb-8">
              Credentials That Matter
            </h2>
            <div className="space-y-4">
              {credentials.map((c, i) => (
                <div key={i} className="flex items-center gap-4 border border-[#1F2937] rounded-sm p-5 bg-[#0F1A15]" data-testid={`credential-${i}`}>
                  <c.icon className="w-6 h-6 text-[#D95D39] shrink-0" />
                  <span className="text-[#F8FAFC] font-semibold text-sm sm:text-base">{c.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Service Area */}
          <div className="border border-[#1F2937] rounded-sm p-8 sm:p-10 bg-[#0F1A15] flex items-start gap-4" data-testid="about-service-area">
            <Clock className="w-8 h-8 text-[#D95D39] shrink-0 mt-1" />
            <div>
              <h3 className="text-[#F8FAFC] font-bold text-lg mb-2">Serving St. Johns and Flagler Counties — and Proud of It.</h3>
              <p className="text-[#9CA3AF] text-sm leading-relaxed">
                We know these neighborhoods, these woods, these animals. When you call a local guy with 50 years on the job, you get someone who doesn't need GPS to find your property and doesn't need a manual to trap what's on it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F1A15] border-t border-[#1F2937] py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-[#F8FAFC] font-black text-2xl sm:text-3xl tracking-tighter mb-4">
            Let's Talk About Your Situation
          </h2>
          <p className="text-[#9CA3AF] text-sm sm:text-base mb-8">
            Call D. Davis directly or get a free quote online.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:9042376253" data-testid="about-call-btn">
              <button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-black uppercase tracking-widest text-sm px-8 py-4 rounded-sm btn-glow inline-flex items-center gap-3 transition-colors duration-150">
                <Phone className="w-5 h-5" /> Call 904-237-6253
              </button>
            </a>
            <Link to="/contact" data-testid="about-quote-btn">
              <button className="border border-[#1F2937] hover:border-[#D95D39] text-[#F8FAFC] font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-sm inline-flex items-center gap-3 transition-colors duration-150">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
